import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import UserDataService from "../services/UserService";
import { useTable, useSortBy, usePagination } from "react-table";
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  MenuItem,
  Chip,
  InputAdornment,
  Stack,
  alpha
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CreditScoreIcon from "@mui/icons-material/CreditScore";

const President = (props) => {
  const [users, setUsers] = useState([]);
  const [searchTitle, setSearchTitle] = useState("");
  const usersRef = useRef();
  usersRef.current = users;

  useEffect(() => {
    retrieveUsers();
  }, []); // eslint-disable-line

  const retrieveUsers = () => {
    UserDataService.presGetAll()
      .then((response) => {
        const u = response.data || [];
        setUsers(u);
      })
      .catch((e) => console.log(e));
  };

  const onChangeSearchTitle = (e) => {
    const q = e.target.value;
    setSearchTitle(q);
    findByTitle(q);
  };

  const findByTitle = (qArg) => {
    const q = (qArg ?? searchTitle).toString().trim().toLowerCase();
    if (!q) {
      setUsers(usersRef.current || []);
      return;
    }
    const all = usersRef.current || [];
    const filtered = all.filter((u) => {
      const fname = ((u.firstName || u.firstname || u.first_name) || "").toString().toLowerCase();
      const lname = ((u.lastName || u.lastname || u.last_name) || "").toString().toLowerCase();
      const uname = (u.username || "").toString().toLowerCase();
      const reg = ((u.registrationNo || u.membershipId) || "").toString().toLowerCase();
      return fname.includes(q) || lname.includes(q) || uname.includes(q) || reg.includes(q);
    });
    setUsers(filtered);
  };

  const renderAccountLink = (path, id) => (
    <Link to={`/dashboard/${path}/${id}`} onClick={(e) => e.stopPropagation()} style={{ textDecoration: "none" }}>
      <Chip
        label="View"
        size="small"
        clickable
        sx={{
          height: 24,
          fontSize: "0.75rem",
          fontWeight: 600,
          backgroundColor: alpha("#0B3B60", 0.08),
          color: "#0B3B60",
          borderRadius: "6px",
          border: "1px solid",
          borderColor: alpha("#0B3B60", 0.2),
          "&:hover": {
            backgroundColor: "#0B3B60",
            color: "#ffffff",
            borderColor: "#0B3B60",
          },
          transition: "all 0.15s ease-in-out",
        }}
      />
    </Link>
  );

  const columns = useMemo(
    () => [
      {
        Header: "First Name",
        accessor: (row) => row.firstName || row.firstname || row.first_name || "",
        disableSortBy: false,
        sortType: "basic",
        Cell: ({ value, row }) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>
              {value || "—"}
            </Typography>
            {(row.original.registrationNo || row.original.membershipId) && (
              <Typography variant="caption" sx={{ color: "#64748B", display: "block" }}>
                {row.original.registrationNo ? `Reg: ${row.original.registrationNo}` : `ID: ${row.original.membershipId}`}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        Header: "Last Name",
        accessor: (row) => row.lastName || row.lastname || row.last_name || "",
        disableSortBy: false,
        sortType: "basic",
        Cell: ({ value }) => (
          <Typography variant="body2" sx={{ color: "#334155", fontWeight: 600 }}>
            {value || "—"}
          </Typography>
        ),
      },
      {
        Header: "Shares",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderAccountLink("sharespr", value),
      },
      {
        Header: "Savings",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderAccountLink("savingspr", value),
      },
      {
        Header: "Loan",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderAccountLink("loanpr", value),
      },
      {
        Header: "Ileya",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderAccountLink("ileyasavingspr", value),
      },
      {
        Header: "Dev. Levy",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderAccountLink("devpr", value),
      },
      {
        Header: "Minutes",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderAccountLink("minpr", value),
      },
      {
        Header: "Welfare",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderAccountLink("welpr", value),
      },
      {
        Header: "Takaful",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderAccountLink("takafulpr", value),
      },
      {
        Header: "Special",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderAccountLink("speaccpr", value),
      },
      {
        Header: "Business",
        accessor: (row) => row._id,
        Cell: ({ value }) => renderAccountLink("businesspr", value),
      },
    ],
    [] // eslint-disable-line
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state: { pageIndex, pageSize },
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    canNextPage,
    canPreviousPage,
    pageOptions,
    pageCount,
  } = useTable(
    {
      columns,
      data: users,
      initialState: { pageIndex: 0, pageSize: 15 },
    },
    useSortBy,
    usePagination
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 2.5,
          border: "1px solid #E2E8F0",
          backgroundColor: "#ffffff",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03)",
        }}
      >
        <Grid container alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "10px",
                  backgroundColor: alpha("#0B3B60", 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#0B3B60",
                }}
              >
                <AccountBalanceWalletIcon />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0F172A", letterSpacing: "-0.01em" }}>
                  Members & Financial Accounts
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748B" }}>
                  Executive oversight: Browse member accounts and access all financial sub-ledgers ({users.length} members total).
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={7} sx={{ textAlign: { xs: "left", md: "right" } }}>
            <Box sx={{ display: "inline-flex", gap: 1.2, alignItems: "center", width: "100%", justifyContent: { md: "flex-end" }, flexWrap: "wrap" }}>
              <Button
                component={Link}
                to="/dashboard/business-overview"
                size="small"
                variant="outlined"
                startIcon={<StorefrontIcon fontSize="small" />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "8px",
                  borderColor: "#0D9488",
                  color: "#0D9488",
                  backgroundColor: "rgba(13, 148, 136, 0.04)",
                  "&:hover": { backgroundColor: "rgba(13, 148, 136, 0.1)", borderColor: "#0F766E" },
                }}
              >
                Business Overview
              </Button>
              <Button
                component={Link}
                to="/dashboard/loan-overview"
                size="small"
                variant="outlined"
                startIcon={<CreditScoreIcon fontSize="small" />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "8px",
                  borderColor: "#D97706",
                  color: "#D97706",
                  backgroundColor: "rgba(217, 119, 6, 0.04)",
                  "&:hover": { backgroundColor: "rgba(217, 119, 6, 0.1)", borderColor: "#B45309" },
                }}
              >
                Loan Overview
              </Button>
              <TextField
                size="small"
                placeholder="Search by first name, last name, or reg #..."
                value={searchTitle}
                onChange={onChangeSearchTitle}
                onBlur={() => { if (!searchTitle) retrieveUsers(); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#94A3B8" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: { xs: "100%", sm: 280 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#F8FAFC",
                    "& fieldset": { borderColor: "#E2E8F0" },
                    "&:hover fieldset": { borderColor: "#CBD5E1" },
                    "&.Mui-focused fieldset": { borderColor: "#0B3B60" },
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 1 }}>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: "1px solid #E2E8F0",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Table size="small" {...getTableProps()}>
              <TableHead sx={{ backgroundColor: "#0B3B60" }}>
                {headerGroups.map((headerGroup) => (
                  <TableRow key={headerGroup.getHeaderGroupProps().key} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <TableCell
                        key={column.getHeaderProps().key}
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.8125rem",
                          color: "#ffffff",
                          backgroundColor: "#0B3B60",
                          borderBottom: "1px solid rgba(255,255,255,0.1)",
                          py: 1.5,
                          cursor: column.canSort ? "pointer" : "default",
                          userSelect: "none",
                        }}
                      >
                        {column.render("Header")}
                        {column.isSorted ? (column.isSortedDesc ? " ↓" : " ↑") : ""}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>

              <TableBody {...getTableBodyProps()}>
                {page.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 5, color: "#64748B" }}>
                      No member accounts found.
                    </TableCell>
                  </TableRow>
                ) : (
                  page.map((row) => {
                    prepareRow(row);
                    return (
                      <TableRow
                        hover
                        key={row.getRowProps().key}
                        {...row.getRowProps()}
                        sx={{
                          "&:nth-of-type(even)": { backgroundColor: "#F8FAFC" },
                          "&:hover": { backgroundColor: "#F1F5F9 !important" },
                          "& td": { py: 1.2, borderColor: "#F1F5F9" },
                        }}
                      >
                        {row.cells.map((cell) => (
                          <TableCell key={cell.getCellProps().key} {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Developer-grade pagination bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 2.5,
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
                sx={{ borderRadius: "6px", textTransform: "none", fontWeight: 600, borderColor: "#E2E8F0" }}
              >
                First
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                sx={{ borderRadius: "6px", textTransform: "none", fontWeight: 600, borderColor: "#E2E8F0" }}
              >
                Previous
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => nextPage()}
                disabled={!canNextPage}
                sx={{ borderRadius: "6px", textTransform: "none", fontWeight: 600, borderColor: "#E2E8F0" }}
              >
                Next
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
                sx={{ borderRadius: "6px", textTransform: "none", fontWeight: 600, borderColor: "#E2E8F0" }}
              >
                Last
              </Button>
            </Stack>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 500 }}>
                Page <strong style={{ color: "#0F172A" }}>{pageIndex + 1}</strong> of{" "}
                <strong style={{ color: "#0F172A" }}>{pageOptions.length || 1}</strong>
              </Typography>
              <TextField
                select
                size="small"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                sx={{
                  width: 130,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    fontSize: "0.8125rem",
                  },
                }}
              >
                {[10, 15, 25, 50, 100].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size} / page
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default President;
